import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { cn } from "@pontimed/ui";
import { Link } from "@tanstack/react-router";
import { AnimatePresence, m } from "framer-motion";
import {
  AlarmClock,
  AlertCircle,
  AlertTriangle,
  BadgeCheck,
  Ban,
  Bell,
  Building2,
  CalendarCheck,
  CalendarX,
  Check,
  CheckCheck,
  CheckCircle2,
  CreditCard,
  Inbox,
  Info,
  Loader2,
  Mail,
  RefreshCw,
  Settings,
  Sparkles,
  Star,
  Stethoscope,
  Users,
  X,
  XCircle,
  type LucideIcon,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { LoadErrorState } from "@/components/ui/load-error-state";
import {
  SwipeableTabsContent,
  type SwipeableTabsHandle,
} from "@/components/ui/swipeable-tabs-content";
import { TabBar, type TabItem } from "@/components/ui/tab-bar";
import { notificationLinkHref } from "@/lib/admin-access";
import { showToast } from "@/lib/ui/toast";

export type NotificationsTab = "all" | "actionRequired" | "informational";
export type WorkspaceType = "user" | "provider" | "facility" | "business" | "admin";

const VALID_TABS: readonly NotificationsTab[] = ["all", "actionRequired", "informational"] as const;
const PAGE_SIZE = 20;

interface LocalizedString {
  ro?: string;
  en?: string;
  bg?: string;
  [key: string]: string | undefined;
}

interface NotificationItem {
  id: string;
  type: string;
  severity: "informational" | "action_required";
  title: LocalizedString;
  body: LocalizedString;
  data: {
    link?: string;
    entityType?: string;
    action?: string;
    reasons?: string[];
    customMessage?: string;
  } | null;
  readAt: string | null;
  createdAt: string;
}

interface NotificationsResponse {
  notifications: NotificationItem[];
  total: number;
  unreadCount: number;
}

type InvitationType = "team" | "staff" | "affiliation" | "seat";

interface Invitation {
  id: string;
  type: InvitationType;
  fromName: string;
  role: string;
  invitedAt: string;
  facilityId?: string;
  facilityName?: string;
}

interface InvitationsResponse {
  invitations: Invitation[];
  count: number;
}

// Icon tiles: `*-soft` surface + `*-strong` glyph (AA in both themes). Full
// class strings so Tailwind's scanner keeps them.
const TONE_CLASSES = {
  primary: "bg-primary-soft text-secondary-foreground",
  success: "bg-success-soft text-success-strong",
  warning: "bg-warning-soft text-warning-strong",
  destructive: "bg-destructive-soft text-destructive-strong",
  info: "bg-info-soft text-info-strong",
  accent: "bg-accent-soft text-accent-strong",
} as const;
type Tone = keyof typeof TONE_CLASSES;

const INVITATION_TYPE_CONFIG: Record<InvitationType, { icon: LucideIcon; tone: Tone }> = {
  team: { icon: Users, tone: "accent" },
  staff: { icon: Building2, tone: "warning" },
  affiliation: { icon: Stethoscope, tone: "primary" },
  seat: { icon: CreditCard, tone: "info" },
};

// Lucide icons (not emoji): consistent across OSes and silent for screen
// readers — the tile is decorative, the title carries the meaning.
const TYPE_ICONS: Record<string, { icon: LucideIcon; tone: Tone }> = {
  suggestion_approved: { icon: CheckCircle2, tone: "success" },
  suggestion_rejected: { icon: XCircle, tone: "destructive" },
  claim_approved: { icon: BadgeCheck, tone: "success" },
  claim_rejected: { icon: Ban, tone: "destructive" },
  admin_message: { icon: Mail, tone: "info" },
  appointment_confirmed: { icon: CalendarCheck, tone: "success" },
  appointment_cancelled: { icon: CalendarX, tone: "destructive" },
  appointment_reminder: { icon: AlarmClock, tone: "warning" },
  review_request: { icon: Star, tone: "warning" },
  "provider.status.approved": { icon: BadgeCheck, tone: "success" },
  "provider.status.suspended": { icon: AlertTriangle, tone: "warning" },
  "provider.status.reactivated": { icon: RefreshCw, tone: "success" },
  "provider.status.registered": { icon: Sparkles, tone: "accent" },
  "provider.action_required": { icon: AlertTriangle, tone: "warning" },
};
const DEFAULT_TYPE_ICON: { icon: LucideIcon; tone: Tone } = { icon: Bell, tone: "info" };

const ACTION_REASON_LABELS: Record<string, MessageDescriptor> = {
  missing_documents: msg`Missing documents — please upload the required documents`,
  illegible_documents: msg`Illegible documents — please upload clearer versions`,
  incorrect_data: msg`Incorrect data — please verify and correct your information`,
  incomplete_profile: msg`Incomplete profile — please fill in all required fields`,
  photo_required: msg`Photo required — please add a profile photo`,
  license_mismatch: msg`License number mismatch — please verify your credentials`,
};

interface NotificationsViewProps {
  workspaceType: WorkspaceType;
  workspaceId?: string;
  activeTab: NotificationsTab;
  onTabChange: (tab: NotificationsTab) => void;
}

/**
 * Per-workspace invitation scope mirrors legacy `notifications-client.tsx`:
 *   user      → all 4 types
 *   provider  → team + affiliation
 *   facility  → staff (filtered by workspaceId when provided)
 *   business  → none
 */
function scopeInvitations(
  all: Invitation[],
  workspaceType: WorkspaceType,
  workspaceId: string | undefined,
): Invitation[] {
  if (workspaceType === "user") return all;
  if (workspaceType === "provider")
    return all.filter((i) => i.type === "team" || i.type === "affiliation");
  if (workspaceType === "facility")
    return all.filter((i) => i.type === "staff" && (!workspaceId || i.facilityId === workspaceId));
  return [];
}

export function NotificationsView({
  workspaceType,
  workspaceId,
  activeTab,
  onTabChange,
}: NotificationsViewProps) {
  const { t, i18n } = useLingui();
  const locale = i18n.locale;
  const swipeRef = useRef<SwipeableTabsHandle>(null);

  type ReadFilter = "unread" | "read";
  const [readFilter, setReadFilter] = useState<ReadFilter>("unread");
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  // P16: a failed load must not fall through to "You're all caught up" —
  // that would also hide pending invitations that need an answer.
  const [loadError, setLoadError] = useState(false);
  const [invitationsError, setInvitationsError] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());

  const fetchNotifications = useCallback(
    async (offset: number, append: boolean, status: ReadFilter) => {
      if (append) setLoadingMore(true);
      else setLoading(true);
      try {
        const params = new URLSearchParams({
          workspaceType,
          limit: String(PAGE_SIZE),
          offset: String(offset),
          status,
        });
        if (workspaceId) params.set("workspaceId", workspaceId);
        const data = await apiFetch.get<NotificationsResponse>(
          `/api/account/notifications?${params.toString()}`,
        );
        setItems((prev) =>
          append ? [...prev, ...(data.notifications ?? [])] : (data.notifications ?? []),
        );
        setTotal(data.total ?? 0);
        setUnreadCount(data.unreadCount ?? 0);
        if (!append) setLoadError(false);
      } catch {
        if (append) showToast(t`Could not load more notifications`, "error");
        else setLoadError(true);
      }
      setNow(Date.now());
      setLoading(false);
      setLoadingMore(false);
    },
    [workspaceType, workspaceId, t],
  );

  const fetchInvitations = useCallback(async () => {
    try {
      const data = await apiFetch.get<InvitationsResponse>("/api/account/invitations");
      setInvitations(scopeInvitations(data.invitations ?? [], workspaceType, workspaceId));
      setInvitationsError(false);
    } catch {
      setInvitationsError(true);
    }
  }, [workspaceType, workspaceId]);

  const retryAll = () => {
    fetchNotifications(0, false, readFilter);
    fetchInvitations();
  };

  useEffect(() => {
    fetchNotifications(0, false, readFilter);
    fetchInvitations();
  }, [fetchNotifications, fetchInvitations, readFilter]);

  /** Human-readable failure copy for an invitation accept/decline. Maps the
   *  API status instead of echoing the server's English `error` string. */
  const invitationErrorMessage = (err: unknown): string => {
    if (err instanceof ApiError) {
      if (err.status === 401) return t`Your session expired. Please sign in again.`;
      if (err.status === 404) return t`This invitation no longer exists.`;
      if (err.status === 409) return t`This invitation was already answered.`;
      if (err.status === 403) return t`You are not allowed to answer this invitation.`;
    }
    return t`Could not respond to the invitation. Please try again.`;
  };

  const handleInvitationAction = async (invitation: Invitation, action: "accept" | "decline") => {
    setActionLoading(invitation.id);
    const toast = showToast(
      action === "accept" ? t`Accepting invitation…` : t`Declining invitation…`,
      "loading",
    );
    try {
      switch (invitation.type) {
        // Team + affiliation answers go to dedicated invitee-only sub-routes.
        // The shared PATCH endpoints multiplex the invitee with an owner /
        // facility-admin audience, so they stay behind the 2FA step-up gate —
        // which used to 403 every invitee who had not enrolled TOTP yet.
        case "affiliation":
          await apiFetch.post(
            action === "accept"
              ? "/api/account/facility-providers/accept"
              : "/api/account/facility-providers/decline",
            { id: invitation.id },
          );
          break;
        case "team":
          await apiFetch.post(
            action === "accept"
              ? "/api/account/provider-team/accept"
              : "/api/account/provider-team/decline",
            { id: invitation.id },
          );
          break;
        case "staff":
          if (!invitation.facilityId) {
            toast.update(t`This invitation is incomplete. Please refresh and try again.`, "error");
            return;
          }
          await apiFetch.post(
            action === "accept"
              ? "/api/account/facility-staff/accept"
              : "/api/account/facility-staff/decline",
            { facilityId: invitation.facilityId },
          );
          break;
        case "seat":
          await apiFetch.patch("/api/account/seats", { id: invitation.id, action });
          break;
        default:
          toast.dismiss();
          return;
      }
      setInvitations((prev) => prev.filter((inv) => inv.id !== invitation.id));
      toast.update(
        action === "accept" ? t`Invitation accepted` : t`Invitation declined`,
        "success",
      );
      // Reconcile against the server (the card is removed above for instant
      // feedback). Notifications are a separate feed and are left alone, so
      // answering an invite never flashes the list back into skeletons.
      fetchInvitations();
    } catch (err) {
      toast.update(invitationErrorMessage(err), "error");
      // Reconcile: the row may already have been answered elsewhere (another
      // device, the deep link). Re-pull so the card disappears if it is gone.
      fetchInvitations();
    } finally {
      setActionLoading(null);
    }
  };

  const handleMarkRead = async (id: string) => {
    try {
      await apiFetch.patch("/api/account/notifications/mark-read", { id });
      fetchNotifications(0, false, readFilter);
    } catch {
      showToast(t`Could not mark the notification as read`, "error");
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await apiFetch.patch("/api/account/notifications/mark-read", {
        all: true,
        workspaceType,
        ...(workspaceId ? { workspaceId } : {}),
      });
      fetchNotifications(0, false, readFilter);
    } catch {
      showToast(t`Could not mark the notifications as read`, "error");
    }
  };

  const getText = (field: LocalizedString) =>
    field[locale] || field.en || field.ro || Object.values(field).find((v) => !!v) || "";

  const timeAgo = useMemo(
    () => (date: string) => {
      const diff = now - new Date(date).getTime();
      const minutes = Math.floor(diff / 60000);
      if (minutes < 1) return t`now`;
      if (minutes < 60) return t`${minutes}m`;
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return t`${hours}h`;
      const days = Math.floor(hours / 24);
      if (days < 7) return t`${days}d`;
      return new Date(date).toLocaleDateString(locale, { day: "2-digit", month: "short" });
    },
    [now, locale, t],
  );

  const filterByTab = useCallback(
    (tabKey: NotificationsTab) => {
      if (tabKey === "actionRequired") return items.filter((n) => n.severity === "action_required");
      if (tabKey === "informational") return items.filter((n) => n.severity === "informational");
      return items;
    },
    [items],
  );

  const hasMore = items.length < total;
  const showInvitationsForTab = (tabKey: NotificationsTab) =>
    tabKey === "all" || tabKey === "actionRequired";

  const tabs: TabItem<NotificationsTab>[] = [
    { id: "all", label: t`All`, icon: <Inbox className="size-4" /> },
    {
      id: "actionRequired",
      label: t`Action required`,
      icon: <AlertCircle className="size-4" />,
      trailing:
        invitations.length > 0 ? (
          <span className="bg-primary text-primary-foreground inline-flex size-5 items-center justify-center rounded-full text-[0.625rem] font-bold">
            {invitations.length}
          </span>
        ) : undefined,
    },
    { id: "informational", label: t`Info`, icon: <Info className="size-4" /> },
  ];

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      {/* mt-2 + the parent's gap-4 = the canonical 24px header→tabs gap. */}
      <TabBar
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={onTabChange}
        onActiveTabClick={() => swipeRef.current?.scrollActiveToTop()}
        className="mt-2"
      />

      <div className="flex items-center justify-end gap-2">
        <div className="border-border flex shrink-0 overflow-hidden rounded-lg border">
          <button
            type="button"
            onClick={() => setReadFilter("unread")}
            aria-pressed={readFilter === "unread"}
            className={cn(
              "flex min-h-11 items-center gap-1 px-3 py-1.5 text-xs font-medium transition-colors",
              readFilter === "unread"
                ? "bg-primary/10 text-primary"
                : "text-muted-foreground hover:bg-muted",
            )}
          >
            <Trans>Unread</Trans>
            {unreadCount > 0 && (
              <span className="bg-primary/20 ml-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[0.5rem] font-bold">
                {unreadCount}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => setReadFilter("read")}
            aria-pressed={readFilter === "read"}
            className={cn(
              "flex min-h-11 items-center gap-1 px-3 py-1.5 text-xs font-medium transition-colors",
              readFilter === "read"
                ? "bg-primary/10 text-primary"
                : "text-muted-foreground hover:bg-muted",
            )}
          >
            <Trans>Read</Trans>
          </button>
        </div>

        {unreadCount > 0 && (
          <>
            {/* Phones get the same action as a 44px icon button. */}
            <Button
              variant="ghost"
              size="icon-touch"
              onClick={handleMarkAllRead}
              aria-label={t`Mark all read (${unreadCount})`}
              className="text-muted-foreground sm:hidden"
            >
              <CheckCheck className="size-5" />
            </Button>
            <Button
              variant="ghost"
              size="touch"
              onClick={handleMarkAllRead}
              className="text-muted-foreground hidden text-xs sm:inline-flex"
            >
              <CheckCheck className="size-4" />
              <Trans>Mark all read ({unreadCount})</Trans>
            </Button>
          </>
        )}
      </div>

      <SwipeableTabsContent
        ref={swipeRef}
        activeTab={activeTab}
        tabIds={VALID_TABS}
        onTabChange={onTabChange}
        renderPanel={(panelKey) => {
          const panelItems = filterByTab(panelKey);
          const showInvitations = showInvitationsForTab(panelKey);
          if (loading) return <NotificationsSkeleton />;

          return (
            <div className="space-y-3 pb-6">
              {loadError && (
                <LoadErrorState
                  title={<Trans>Could not load notifications.</Trans>}
                  onRetry={retryAll}
                />
              )}
              {showInvitations && invitationsError && !loadError && (
                <LoadErrorState
                  title={<Trans>Could not load your invitations.</Trans>}
                  onRetry={fetchInvitations}
                />
              )}
              {showInvitations && invitations.length > 0 && (
                <AnimatePresence>
                  {invitations.map((inv, index) => {
                    const config = INVITATION_TYPE_CONFIG[inv.type];
                    const Icon = config.icon;
                    const isLoading = actionLoading === inv.id;
                    return (
                      <m.div
                        key={`inv-${inv.id}`}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, x: -100 }}
                        transition={{ delay: index * 0.05 }}
                        layout
                        className="border-border bg-card rounded-xl border p-4"
                      >
                        <div className="flex items-start gap-3 sm:gap-4">
                          <div
                            className={cn(
                              "flex size-10 shrink-0 items-center justify-center rounded-xl sm:size-12",
                              TONE_CLASSES[config.tone],
                            )}
                            aria-hidden
                          >
                            <Icon className="size-5 sm:size-6" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <p className="text-foreground text-sm font-semibold sm:text-base">
                                  {inv.fromName}
                                </p>
                                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                                  <span className="bg-muted text-muted-foreground inline-flex items-center rounded-md px-2 py-0.5 text-[0.625rem] font-medium">
                                    {inv.type === "team" && <Trans>Team member</Trans>}
                                    {inv.type === "staff" && <Trans>Facility staff</Trans>}
                                    {inv.type === "affiliation" && <Trans>Affiliation</Trans>}
                                    {inv.type === "seat" && <Trans>Seat</Trans>}
                                  </span>
                                  <span className="bg-muted text-muted-foreground inline-flex items-center rounded-md px-2 py-0.5 text-[0.625rem] font-medium">
                                    {inv.role}
                                  </span>
                                  <span className="border-primary/30 text-primary inline-flex items-center rounded-md border px-2 py-0.5 text-[0.625rem] font-medium">
                                    <Trans>Action needed</Trans>
                                  </span>
                                </div>
                              </div>
                              <span className="text-muted-foreground shrink-0 text-xs">
                                {timeAgo(inv.invitedAt)}
                              </span>
                            </div>
                            <div className="mt-3 flex items-center gap-2">
                              <Button
                                size="touch"
                                onClick={() => handleInvitationAction(inv, "accept")}
                                disabled={isLoading}
                                className="min-w-[80px]"
                              >
                                {isLoading ? (
                                  <Loader2 className="size-4 animate-spin" />
                                ) : (
                                  <>
                                    <Check className="size-4" />
                                    <Trans>Accept</Trans>
                                  </>
                                )}
                              </Button>
                              <Button
                                variant="outline"
                                size="touch"
                                onClick={() => handleInvitationAction(inv, "decline")}
                                disabled={isLoading}
                                className="min-w-[80px]"
                              >
                                <X className="size-4" />
                                <Trans>Decline</Trans>
                              </Button>
                            </div>
                          </div>
                        </div>
                      </m.div>
                    );
                  })}
                </AnimatePresence>
              )}

              <AnimatePresence>
                {panelItems.map((n, index) => {
                  const isUnread = !n.readAt;
                  const typeIcon = TYPE_ICONS[n.type] ?? DEFAULT_TYPE_ICON;
                  const TypeIcon = typeIcon.icon;
                  const isActionable = n.severity === "action_required";
                  const hasDetails =
                    n.type === "provider.action_required" &&
                    ((n.data?.reasons && n.data.reasons.length > 0) || n.data?.customMessage);
                  const isExpanded = expandedId === n.id;

                  return (
                    <m.div
                      key={n.id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.03 }}
                      className={cn(
                        "border-border bg-card rounded-xl border transition-colors",
                        isUnread && "border-info-border",
                      )}
                    >
                      <div
                        className={cn("flex items-start gap-3 p-4", hasDetails && "cursor-pointer")}
                        onClick={() => {
                          if (hasDetails) setExpandedId(isExpanded ? null : n.id);
                        }}
                      >
                        <span
                          className={cn(
                            "flex size-10 shrink-0 items-center justify-center rounded-xl",
                            TONE_CLASSES[typeIcon.tone],
                          )}
                          aria-hidden
                        >
                          <TypeIcon className="size-5" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-center gap-2">
                              <p
                                className={cn(
                                  "text-foreground text-sm leading-snug",
                                  isUnread ? "font-semibold" : "font-medium",
                                )}
                              >
                                {getText(n.title)}
                              </p>
                              {isActionable && (
                                <span className="border-primary/30 text-primary hidden shrink-0 rounded-full border px-2 py-0.5 text-[0.5625rem] font-semibold sm:inline-flex">
                                  <Trans>Action</Trans>
                                </span>
                              )}
                              {hasDetails && (
                                <span className="text-muted-foreground text-xs">
                                  {isExpanded ? "▲" : "▼"}
                                </span>
                              )}
                            </div>
                            <div className="flex shrink-0 items-center gap-2">
                              <span className="text-muted-foreground text-xs">
                                {timeAgo(n.createdAt)}
                              </span>
                              {isUnread && (
                                <span className="bg-info size-2 shrink-0 rounded-full" />
                              )}
                            </div>
                          </div>
                          <p className="text-muted-foreground mt-0.5 text-sm leading-snug">
                            {getText(n.body)}
                          </p>
                          <div className="mt-2 flex items-center gap-3">
                            {n.data?.link && (
                              <a
                                href={notificationLinkHref(n.data.link)}
                                className="text-secondary-foreground inline-flex min-h-11 items-center gap-1 px-1 text-xs font-medium hover:underline"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Trans>Open</Trans>
                              </a>
                            )}
                            {isUnread && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMarkRead(n.id);
                                }}
                                className="text-muted-foreground hover:text-foreground inline-flex min-h-11 items-center gap-1 px-1 text-xs"
                              >
                                <Check className="size-3" />
                                <Trans>Mark read</Trans>
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      {hasDetails && isExpanded && (
                        <div className="border-warning-border bg-warning-soft border-t px-4 py-3 pl-[4.25rem]">
                          {n.data?.reasons && n.data.reasons.length > 0 && (
                            <ul className="space-y-1.5">
                              {n.data.reasons.map((r) => {
                                const descriptor = ACTION_REASON_LABELS[r];
                                const label = descriptor ? i18n._(descriptor) : r;
                                return (
                                  <li
                                    key={r}
                                    className="text-warning-strong flex items-start gap-2 text-sm"
                                  >
                                    <span className="mt-0.5 shrink-0">•</span>
                                    <span>{label}</span>
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                          {n.data?.customMessage && (
                            <div className="border-warning-border bg-card text-foreground mt-2 rounded-md border px-3 py-2 text-sm italic">
                              &ldquo;{n.data.customMessage}&rdquo;
                            </div>
                          )}
                        </div>
                      )}
                    </m.div>
                  );
                })}
              </AnimatePresence>

              {!loadError &&
                panelItems.length === 0 &&
                (!showInvitations || (invitations.length === 0 && !invitationsError)) && (
                  <div className="border-border bg-card flex flex-col items-center justify-center rounded-xl border py-16">
                    <div className="bg-muted flex size-14 items-center justify-center rounded-2xl">
                      <Inbox className="text-muted-foreground size-7" />
                    </div>
                    <h3 className="text-foreground mt-4 text-sm font-semibold">
                      {panelKey === "actionRequired" ? (
                        <Trans>Nothing needs your attention</Trans>
                      ) : panelKey === "informational" ? (
                        <Trans>No informational updates</Trans>
                      ) : (
                        <Trans>You're all caught up</Trans>
                      )}
                    </h3>
                    <p className="text-muted-foreground mt-1 max-w-xs px-4 text-center text-sm">
                      <Trans>
                        Booking confirmations and status updates appear here as they happen.
                      </Trans>
                    </p>
                  </div>
                )}

              {hasMore && !loadError && (
                <div className="flex justify-center py-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fetchNotifications(items.length, true, readFilter)}
                    disabled={loadingMore}
                    className="min-h-[44px] gap-1.5"
                  >
                    {loadingMore && <Loader2 className="size-4 animate-spin" />}
                    <Trans>Load more</Trans>
                  </Button>
                </div>
              )}

              {/* Footer link — lives inside each panel so it scrolls with content
                  instead of sitting fixed below the swipeable carousel (legacy
                  notifications-client.tsx parity). */}
              <div className="flex justify-center pt-4">
                <NotificationSettingsLink workspaceType={workspaceType} workspaceId={workspaceId} />
              </div>
            </div>
          );
        }}
      />
    </div>
  );
}

const SETTINGS_LINK_CLASS =
  "text-muted-foreground hover:text-foreground flex min-h-[44px] items-center gap-2 text-sm font-medium transition-colors";

/**
 * Footer link to the preferences that match the workspace: a clinic opens its
 * own facility matrix, a doctor the provider matrix. Business and admin have
 * no workspace-level matrix yet, so they get the personal settings — labelled
 * as personal so nobody thinks they are editing the workspace's alerts.
 */
function NotificationSettingsLink({
  workspaceType,
  workspaceId,
}: {
  workspaceType: WorkspaceType;
  workspaceId?: string;
}) {
  const icon = <Settings className="size-4" aria-hidden />;
  if (workspaceType === "facility" && workspaceId) {
    return (
      <Link
        to="/account/facility/$facilityId/settings/notifications"
        params={{ facilityId: workspaceId }}
        className={SETTINGS_LINK_CLASS}
      >
        {icon}
        <Trans>Notification preferences</Trans>
      </Link>
    );
  }
  if (workspaceType === "provider") {
    return (
      <Link
        to="/account/provider/settings"
        search={{ tab: "notifications" }}
        className={SETTINGS_LINK_CLASS}
      >
        {icon}
        <Trans>Notification preferences</Trans>
      </Link>
    );
  }
  return (
    <Link
      to="/account/user/settings"
      search={{ tab: "notifications" }}
      className={SETTINGS_LINK_CLASS}
    >
      {icon}
      {workspaceType === "user" ? (
        <Trans>Notification settings</Trans>
      ) : (
        <Trans>Personal notification settings</Trans>
      )}
    </Link>
  );
}

function NotificationsSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="border-border bg-card animate-pulse rounded-xl border p-4">
          <div className="flex items-start gap-3">
            <div className="bg-muted mt-0.5 size-10 rounded-xl" />
            <div className="flex-1 space-y-2">
              <div className="bg-muted h-4 w-3/4 rounded" />
              <div className="bg-muted h-3 w-1/2 rounded" />
            </div>
            <div className="bg-muted h-3 w-12 rounded" />
          </div>
        </div>
      ))}
    </div>
  );
}

export const NOTIFICATIONS_VALID_TABS = VALID_TABS;
